import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess'
import _ from 'lodash'
import { SportsSeasonDataContext } from 'projectSpecific/sports/cnr/contexts/SportsSeasonDataContext'
import React, { useContext, useEffect, useMemo, useReducer } from 'react'
import { ParentContext } from '../../../../global/cnr/contexts/ParentContext'
import { sidebarHandlers, sidebarInitialState, sidebarMenuTypes, sidebarReducer, sidebarSliderTypes } from '../../../../global/cnr/reducers/SidebarReducer'
import { currentHelpers } from '../../../../global/redirection/current'
import { SportsSeasonContext } from '../../cnr/contexts/SportsSeasonContext'
import { sportsShowTypes } from '../../cnr/reducers/SportsSeasonReducer'
import { sportsStandingsHandlers, sportsStandingsInitialState, sportsStandingsReducer } from '../../cnr/reducers/SportsStandingsReducer'
import { CreatePlayoffContext } from '../../create/CreatePlayoff'
import LevelSectionStandings from './LevelSectionStandings'

const sidebarType = 'leagueStandings'

export const _standingsTypes = {
  section: 'section',
  team: 'team',
  match: 'match',
  playoff: 'playoff'
}

export const SportsStandingsContext = React.createContext()

const LeagueStandings = (props) => {

  // appUserAccess
  const appUserAccess = useAppUserAccess()
  const { favs_info } = appUserAccess ?? {}
  const { eventFavs } = favs_info ?? {};
  const _teamsFavs = eventFavs && eventFavs['teams']

  // componentContexts
  const { componentContexts, standingType, forOverview, forPlayoff } = props

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { appUser_state, paps_state } = states ?? {}
  const { pathViews, viewKey, storageItemPage } = paps_state ?? {}
  const { appUsers } = appUser_state ?? {}

  const _teamKey_page = pathViews?.teams
  const _matchKey_page = pathViews?.matches

  let _standingType = standingType;

  if (_teamKey_page) {
    _standingType = _standingsTypes.team
  } else if (_matchKey_page) {
    _standingType = _standingsTypes.match
  }


  // componentContexts
  const { displayContext } = componentContexts ?? {}
  const { display_state } = displayContext ?? {}
  const { displayProps } = display_state ?? {}
  const { selectedGroupItem } = displayProps ?? {}

  const sportsSeasonDataContext = useContext(SportsSeasonDataContext)
  const { sportsSeasonData_state } = sportsSeasonDataContext ?? {}

  // sportsSeasonContext
  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state, sportsSeason_fns, sportsSeason_handlers } = sportsSeasonContext ?? {}
  const { sportsMode, districts_info, sportDistricts, teams_info, details_info, matches_info, currents, sportsShowType, gameScoringType } = sportsSeason_state ?? {}
  const { teams } = teams_info ?? {}
  const { matches, matches_playoffs } = matches_info ?? {}
  const { organization: districts_org } = districts_info ?? {}
  const { details, sectionPlayoffTeams } = details_info ?? {}
  const { playoffTeamsPerSection } = details ?? {}

  // createPlayoffContext
  const createPlayoffContext = useContext(CreatePlayoffContext);
  const { createPlayoff_state } = createPlayoffContext ?? {}
  const { playoff_info } = createPlayoff_state ?? {}
  const { playoffLevel, playoffLevelTeams } = playoff_info ?? {}


  const init_state = {}
  const [sportsStandings_state, sportsStandings_dispatch] = useReducer(sportsStandingsReducer, init_state, sportsStandingsInitialState)
  const sportsStandings_handlers = useMemo(
    () => sportsStandingsHandlers(sportsStandings_dispatch),
    [sportsStandings_dispatch]
  );

  const {
    noData,
    selected_teams,
    selectedLevelKey,
    selectedTeamLevel,
    selectedTeams,
    selectedTeamSection,
    showPlayoffPicture,
    swiper,
  } = sportsStandings_state ?? {}

  const [sidebar_state, sidebar_dispatch] = useReducer(sidebarReducer, sidebarInitialState({ sidebarType, handlers: sportsSeason_handlers }));
  const sidebar_handlers = sidebarHandlers(sidebar_dispatch)
  const { sidebar_items } = sidebar_state ?? {}
  const { setInit } = sidebar_handlers ?? {}

  const _showPlayoffPicture = sportsShowType ? sportsShowType === sportsShowTypes.fullStandings : showPlayoffPicture

  const { ms_levels } = sidebar_items ?? {}

  const _initProps = { sidebarSliderType: sidebarSliderTypes.labeled }

  const plts = playoffLevelTeams ? (lk) => (lk + ' (' + playoffLevelTeams[lk].length + ')') : {}

  // memo - districts
  const _districts = useMemo(() => {
    if (!sportsMode.districtSports || sportsMode.matches || sportsMode.sports || !sportDistricts || !districts_org) {
      return null;
    }
    return sportDistricts
      .map(sd => districts_org[sd]?.name)
      .filter(Boolean); // Remove undefined/null values
  }, [sportsMode.districtSports, sportsMode.matches, sportsMode.sports, sportDistricts, districts_org]);

  // memo - _levelData
  const _levelData = useMemo(() => {
    if (!selectedTeams || Object.keys(selectedTeams).length === 0) return null;

    const _levelTeams = _.groupBy(selectedTeams, 'levels');
    if (_levelTeams.Non) delete _levelTeams.Non;

    const levelKeys = selectedTeamLevel ? [selectedTeamLevel] : Object.keys(_levelTeams).sort().reverse();

    return { _levelTeams, levelKeys };
  }, [selectedTeams, selectedTeamLevel, playoffLevel]);

  // memo - _captions
  const _captions = useMemo(() => {
    if (!forPlayoff || !ms_levels) return null;
    return ms_levels.items.reduce((acc, lk) => {
      acc[lk] = plts(lk);
      return acc;
    }, {});
  }, []);

  // memo - _selectedTeams
  const _selectedTeams = useMemo(() => {
    if (!teams) return null;
    if (selectedGroupItem) {
      return sportsSeason_fns.getTeamsByLevel(selectedGroupItem);
    } else if (_teamKey_page && teams[_teamKey_page]) {
      // get the section and level of the selected team
      const { sections, levels } = teams[_teamKey_page];
      // const teams_levelSection = teams_byLevelSection?.[levels]?.[sections]
      const lst = sportsSeason_fns.getTeamsBySection(teams, levels, sections);
      sportsStandings_handlers.handleSet_selectedTeamLevel(levels, sections);
      return lst;
    } else if (_matchKey_page) {
      let _thisMatch = matches?.[_matchKey_page] ?? matches_playoffs?.[_matchKey_page] ?? {};
      if (_thisMatch) {
        const { home, away } = _thisMatch;
        return sportsSeason_fns.getTeamsByMatch(home, away);
      }
    }
    return teams;
  }, [teams, selectedGroupItem, pathViews, matches, matches_playoffs, playoffLevel]);

  // USE MEMO End ************************************

  useEffect(() => {
    if (_districts) {
      currents.districts = currentHelpers.getStorageItem('ms_districts', _districts, storageItemPage);
      setInit({ smt: sidebarMenuTypes.one, items: _districts, currents, as: 'districts' });
    }
  }, [_districts]);

  useEffect(() => {
    if (_levelData) {
      const { levelKeys } = _levelData;
      currents.levels = currentHelpers.getStorageItem('ms_levels', levelKeys, storageItemPage);
      setInit({
        smt: sidebarMenuTypes.two,
        items: levelKeys,
        currents,
        as: 'levels',
        ..._initProps,
        includeSearch: false,
      });
    } else {
      // sportsStandings_handlers.handleNoData(true);
    }
  }, [_levelData]);

  useEffect(() => {
    if (_captions) {
      sportsStandings_handlers.handleAmmend_swiperCaptions(_captions);
    }
  }, [_captions]);

  useEffect(() => {
    if (_selectedTeams) {
      sportsStandings_handlers.handleSet_selectedTeams(_selectedTeams);
    }
  }, [_selectedTeams]);

  return <SportsStandingsContext.Provider value={{ sportsStandings_state, sportsStandings_handlers }}>
    <LevelSectionStandings
      _matchKey_page={_matchKey_page}
      _standingType={_standingType}
      _teamKey_page={_teamKey_page}
      _teamsFavs={_teamsFavs}
      forOverview={forOverview}
      forPlayoff={forPlayoff}
      ms_levels={ms_levels}
      noData={noData}
      playoffLevel={playoffLevel}
      selectedLevelKey={selectedLevelKey}
      selectedTeamLevel={selectedTeamLevel}
      selectedTeams={selectedTeams}
      selectedTeamSection={selectedTeamSection}
      showPlayoffPicture={showPlayoffPicture}
    />
    {/* {_leagueStandings} */}
  </SportsStandingsContext.Provider>

}

export default LeagueStandings